import React, {useEffect} from 'react'
import {alpha, Box, Chip, Grid, Paper, Stack, Typography, useTheme,} from '@mui/material'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import {useLocation, useNavigate} from 'react-router'
import {PATH} from '../../routing/routes'
import {displayDate, displayDuration} from "../../utils";
import {Trip} from "../trips/Trip";
import {TripPhotoGallery} from "./TripPhotoGallery";
import {SectionTitle} from "./SectionTitle";


const SeniorTrip = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const trip = location.state?.trip as Trip | undefined

    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'
    const primaryGreen = theme.palette.primary.main
    const lightGreen = theme.palette.primary.light

    useEffect(() => {
        if (!trip) navigate(PATH.SENIOR, {replace: true})
    }, [trip, navigate])

    if (!trip) return null

    // not used
    // const routeStops = trip.route.split(' - ')

    return (
        <Stack direction="column" sx={{bgcolor: 'background.default', minHeight: '100vh'}}>

            {/* ── HERO ── */}
            <Box sx={{
                background: isDark
                    ? `linear-gradient(135deg, #1a2e1a 0%, ${alpha(primaryGreen, 0.4)} 100%)`
                    : `linear-gradient(135deg, #1c3a1e 0%, ${alpha(primaryGreen, 0.82)} 100%)`,
                color: '#fff',
                px: {xs: 3, md: 6},
                py: {xs: 4, md: 5},
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorative orbs */}
                <Box sx={{
                    position: 'absolute', right: -80, top: -80,
                    width: 320, height: 320, borderRadius: '50%',
                    bgcolor: alpha(lightGreen, 0.12), pointerEvents: 'none',
                }}/>
                <Box sx={{
                    position: 'absolute', right: 80, bottom: -100,
                    width: 200, height: 200, borderRadius: '50%',
                    bgcolor: alpha(lightGreen, 0.07), pointerEvents: 'none',
                }}/>

                {/* Name */}
                <Typography variant="h4" sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700, lineHeight: 1.2,
                    color: '#fff', mb: 2.5, maxWidth: 580,
                }}>
                    {trip.name}
                </Typography>

                {/* Tags */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography variant="body1" sx={{color: '#fff'}}>
                        Durata:
                    </Typography>
                    <Chip
                        icon={<WbSunnyOutlinedIcon/>}
                        label={displayDuration(trip.days.length)}
                        size="small"
                        sx={{
                            bgcolor: alpha('#fff', 0.15), color: '#fff',
                            border: `1px solid ${alpha('#fff', 0.25)}`,
                            fontFamily: '"DM Sans", sans-serif', fontWeight: 500, fontSize: '1rem',
                            '& .MuiChip-icon': {color: 'inherit'},
                        }}
                    />
                </Stack>

                {/* Dates */}
                {trip.dates && trip.dates.length > 0 && (
                    <Stack direction="column" spacing={1} flexWrap="wrap" mt={1.5}>
                        {trip.dates.map((date, i) => (
                            <Stack direction="row" gap="0.5rem" alignItems="center">
                                <CalendarMonthIcon sx={{color: "grey.50"}}/>
                                <Typography
                                    key={i}
                                    variant="h5"
                                    color="grey.50"
                                >
                                    {displayDate(date, trip.days.length)}
                                </Typography>
                            </Stack>

                        ))}
                    </Stack>
                )}

                {/* Price badge */}
                {trip.price !== undefined && (
                    <Box sx={{
                        position: {xs: 'relative', md: 'absolute'},
                        right: {md: 60}, top: {md: '50%'},
                        transform: {md: 'translateY(-50%)'},
                        mt: {xs: 3, md: 0},
                        bgcolor: primaryGreen,
                        borderRadius: '50%',
                        width: 130, height: 130,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 8px 32px ${alpha('#000', 0.3)}`, zIndex: 1,
                    }}>
                        <Typography variant="h5" fontWeight={800} sx={{color: '#fff', lineHeight: 1.1}}>
                            {trip.price}
                        </Typography>
                        <Typography variant="caption" fontWeight={700} sx={{
                            color: alpha('#fff', 0.85), fontSize: '0.62rem', letterSpacing: '0.06em',
                        }}>
                            / PERSOANĂ
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* ── BODY ── */}
            <Box sx={{px: {xs: 2, md: 6}, py: 4}}>

                <Grid container spacing={3}>

                    {/* Itinerary + Notes */}
                    <Grid size={{xs: 12, md: trip.photos && trip.photos.length > 0 ? 5 : 12}}>
                        <Stack spacing={3}>

                            <Paper elevation={0} sx={{
                                border: `1px solid ${alpha(primaryGreen, isDark ? 0.2 : 0.15)}`,
                                borderRadius: 3, p: 3,
                                bgcolor: theme.palette.background.paper,
                            }}>
                                <Typography variant="overline" sx={{
                                    display: 'inline-block',
                                    fontFamily: '"DM Mono", monospace',
                                    fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.12em',
                                    color: theme.palette.text.secondary,
                                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                                    pb: 0.4, mb: 2,
                                }}>
                                    Itinerar
                                </Typography>

                                {trip.days.map((day, dayIndex) => (
                                    <Box key={dayIndex} mb={trip.days.length > 1 ? 3 : 0}>
                                        {trip.days.length > 1 && (
                                            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                                                <Typography variant="overline" sx={{
                                                    fontFamily: '"DM Mono", monospace',
                                                    fontSize: '0.68rem', letterSpacing: '0.1em',
                                                    color: theme.palette.text.secondary,
                                                }}>
                                                    Ziua {dayIndex + 1}
                                                </Typography>
                                            </Stack>
                                        )}

                                        <Box sx={{position: 'relative', pl: 0.5}}>

                                            {day.map((visit, visitIndex) => (
                                                <Box key={visitIndex} sx={{
                                                    display: 'flex', alignItems: 'flex-start',
                                                    mb: visitIndex < day.length - 1 ? 2.5 : 0,
                                                    position: 'relative',
                                                }}>
                                                    <Box sx={{pl: 0, flex: 1}}>
                                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                                            <LocationOnIcon sx={{
                                                                fontSize: 18,
                                                                color: alpha(primaryGreen, 0.8),
                                                                mt: '-1px',
                                                                flexShrink: 0
                                                            }}/>
                                                            <Typography sx={{
                                                                fontFamily: '"DM Sans", sans-serif',
                                                                fontWeight: 600, fontSize: '0.92rem',
                                                                color: theme.palette.text.primary, lineHeight: 1.3,
                                                            }}>
                                                                {visit.locationName}
                                                            </Typography>
                                                        </Stack>
                                                        {visit.details && (
                                                            <Stack direction="row" alignItems="center" spacing={0.5}
                                                                   mt={0.3} ml={3}>
                                                                <InfoOutlinedIcon sx={{
                                                                    fontSize: 12,
                                                                    color: theme.palette.text.secondary
                                                                }}/>
                                                                <Typography variant="caption" sx={{
                                                                    fontFamily: '"DM Sans", sans-serif',
                                                                    color: theme.palette.text.secondary,
                                                                    fontSize: '0.75rem', fontStyle: 'italic',
                                                                }}>
                                                                    {visit.details}
                                                                </Typography>
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                ))}
                            </Paper>

                            {trip.notes && (
                                <Paper elevation={0} sx={{
                                    border: `1px solid ${alpha(primaryGreen, isDark ? 0.2 : 0.15)}`,
                                    borderRadius: 3, p: 3,
                                    bgcolor: theme.palette.background.paper,
                                }}>
                                    <SectionTitle>Despre excursie</SectionTitle>
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        <AutoStoriesIcon sx={{
                                            fontSize: 18, color: alpha(primaryGreen, 0.6),
                                            mt: 0.2, flexShrink: 0,
                                        }}/>
                                        <Typography sx={{
                                            fontFamily: '"DM Sans", sans-serif',
                                            fontSize: '0.875rem', lineHeight: 1.8,
                                            color: theme.palette.text.secondary,
                                            whiteSpace: 'pre-line',
                                        }}>
                                            {trip.notes.trim()}
                                        </Typography>
                                    </Stack>
                                </Paper>
                            )}
                        </Stack>
                    </Grid>

                    {/* Gallery */}
                    {trip.photos && trip.photos.length > 0 && (
                        <Grid size={{xs: 12, md: 7}}>
                            <Stack spacing={3} sx={{position: 'sticky', top: 80}}>

                                {/* Ce include pachetul? */}
                                {trip.included &&
                                    <Paper elevation={0} sx={{
                                        border: `1px solid ${alpha(primaryGreen, isDark ? 0.2 : 0.15)}`,
                                        borderRadius: 3, p: 3,
                                        bgcolor: theme.palette.background.paper,
                                    }}>
                                        <SectionTitle>Ce include pachetul?</SectionTitle>
                                        <Stack spacing={1.5}>
                                            {trip.included.map((included, index) => (
                                                <Stack key={index} direction="row" alignItems="center" spacing={1.25}>
                                                    <Box sx={{
                                                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                                        bgcolor: alpha(primaryGreen, isDark ? 0.2 : 0.12),
                                                        border: `1.5px solid ${alpha(primaryGreen, 0.4)}`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <Typography sx={{
                                                            color: primaryGreen,
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1,
                                                            fontWeight: 700
                                                        }}>✓</Typography>
                                                    </Box>
                                                    <Typography sx={{
                                                        fontFamily: '"DM Sans", sans-serif',
                                                        fontSize: '0.875rem', fontWeight: 500,
                                                        color: theme.palette.text.primary,
                                                    }}>
                                                        {included}
                                                    </Typography>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Paper>
                                }


                                {/* Ce nu e inclus în preț? */}
                                {trip.notIncluded &&
                                    <Paper elevation={0} sx={{
                                        border: `1px solid ${alpha(primaryGreen, isDark ? 0.2 : 0.15)}`,
                                        borderRadius: 3, p: 3,
                                        bgcolor: theme.palette.background.paper,
                                    }}>
                                        <SectionTitle>Ce nu e inclus în preț?</SectionTitle>

                                        <Stack spacing={1.5}>
                                            {trip.notIncluded.map((notIncluded, index) => (
                                                <Stack key={index} direction="row" alignItems="center" spacing={1.25}>
                                                    <Box sx={{
                                                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                                                        bgcolor: alpha(theme.palette.error.main, isDark ? 0.2 : 0.12),
                                                        border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    }}>
                                                        <Typography sx={{
                                                            color: theme.palette.error.main,
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1,
                                                            fontWeight: 700
                                                        }}>✕</Typography>
                                                    </Box>
                                                    <Typography sx={{
                                                        fontFamily: '"DM Sans", sans-serif',
                                                        fontSize: '0.875rem', fontWeight: 500,
                                                        color: theme.palette.text.primary,
                                                    }}>
                                                        {notIncluded}
                                                    </Typography>
                                                </Stack>
                                            ))}

                                        </Stack>
                                    </Paper>
                                }

                                <Paper elevation={0} sx={{
                                    border: `1px solid ${alpha(primaryGreen, isDark ? 0.2 : 0.15)}`,
                                    borderRadius: 3, p: 3,
                                    bgcolor: theme.palette.background.paper,
                                }}>
                                    <SectionTitle>Galerie foto</SectionTitle>
                                    <TripPhotoGallery photos={trip.photos} tripName={trip.name}/>
                                </Paper>

                            </Stack>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Stack>
    )
}

export default SeniorTrip